import React from 'react';
import { observer } from 'mobx-react';

import RegistrationStore from 'onboarding/stores/RegistrationStore';
import { useAsyncFunction } from 'stores/hooks';
import Button from 'ui-brand/components/Button';
import { EmailIcon } from 'ui-brand/components/icons';
import SocialProofPanel from 'ui-brand/components/SocialProofPanel';
import TextInput from 'ui-brand/components/TextInput';

import SignInLink from '../components/SignInLink';
import LegalLinks from './LegalLinks';

type Props = {
  store: RegistrationStore;
  loginUrl: string;
};

function EmailStep({ store, loginUrl }: Props) {
  const [handleSubmit, isSubmitting] = useAsyncFunction(store.submitEmail);

  return (
    <div className="mt-6 rounded-3xl bg-white/70 p-0 sm:p-2 lg:mt-12">
      <div className="grid overflow-hidden rounded-3xl sm:rounded-2xl lg:grid-cols-2">
        <div className="flex items-center rounded-3xl bg-white px-4 py-12 sm:rounded-2xl sm:px-6 lg:min-h-[640px] lg:rounded-r-none lg:px-14">
          <div className="mx-auto w-full max-w-[480px] text-center">
            <p className="text-[13px] font-semibold uppercase tracking-widest text-retino-darkviolet/70">
              {interpolate(gettext('Step %(step)s of 3'), { step: 1 }, true)}
            </p>
            <h1 className="mt-4 text-balance text-[28px] font-semibold leading-tight text-retino-darkviolet lg:text-[32px]">
              {gettext('Try Retino for free')}
            </h1>
            <p className="mt-4 text-base leading-snug text-black">
              {gettext('Returns, claims and shipment tracking in one place.')}
            </p>

            <form
              className="mx-auto mt-12 max-w-[370px] text-left"
              onSubmit={(event) => {
                event.preventDefault();
                handleSubmit();
              }}
            >
              <TextInput
                label={gettext('Work email')}
                placeholder="name@company.com"
                icon={<EmailIcon />}
                type="email"
                autoComplete="email"
                required
                value={store.data.email}
                onChange={store.setEmail}
                error={store.errors.email}
                name="email"
              />
              {store.isEmailTaken && (
                <p className="mt-2 text-[13px] leading-snug text-red-600">
                  {gettext('This email is already used.')}{' '}
                  <a href={loginUrl} className="font-semibold underline">
                    {gettext('Sign in')}
                  </a>
                </p>
              )}
              <div className="mt-4">
                <Button type="submit" variant="primary" size="lg" fullWidth loading={isSubmitting}>
                  {gettext('Continue')}
                </Button>
              </div>
              <p className="mt-4 text-center text-base leading-snug text-retino-darkgrey sm:text-[13px]">
                {gettext('Free for 14 days · No credit card')}
              </p>
            </form>

            <LegalLinks />
            <SignInLink href={loginUrl} />
          </div>
        </div>

        <div className="hidden items-center rounded-2xl bg-retino-sand px-6 py-12 lg:flex lg:rounded-l-none lg:px-12">
          <div className="mx-auto w-full max-w-[560px]">
            <SocialProofPanel />
          </div>
        </div>
      </div>
    </div>
  );
}

export default observer(EmailStep);
